/** @jsxImportSource @emotion/react */

import React from 'react';
import { css } from '@emotion/react';

const RADIAN = Math.PI / 180;

type ChartData = {
  name: string;
  value: number;
  fill: string;
};

type NeedleProps = {
  value: number;
  cx: number;
  cy: number;
  iR: number;
  oR: number;
  color: string;
};

const CustomNeedle = ({ value, cx, cy, iR, oR, color }: NeedleProps) => {
  // Correct angle mapping: 0% = 0°, 100% = 180° (left to right)
  const ang = 180 - (value * 180 / 100);
  const length = (iR + oR) / 2;
  const rad = RADIAN * ang;

  const x0 = cx;
  const y0 = cy;
  const xp = x0 + length * Math.cos(rad);
  const yp = y0 - length * Math.sin(rad);

  // Smaller circle radius for better accuracy
  const circleRadius = 6;

  return (
    <g>
      <circle cx={x0} cy={y0} r={circleRadius} fill={color} stroke="#333" strokeWidth={2} />
      <line
        x1={x0}
        y1={y0}
        x2={xp}
        y2={yp}
        stroke={color}
        strokeWidth={3}
        strokeLinecap="round"
      />
    </g>
  );
};

type CapacityGaugeCardProps = {
  isAnimationActive?: boolean;
  value?: number;
  percentage?: number;
  label?: string;
  subLabel?: string;
  change?: string;
  needleColor?: string;
  usedColor?: string;
  remainingColor?: string;
};

const cardStyles = css`
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  width: 350px;
  margin: 0 auto;
  padding: 24px;
  background: white;
`;

const sectionStyles = css`
  margin-bottom: 16px;
`;

const groupStyles = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
`;

const titleStyles = css`
  font-weight: 600;
  font-size: 18px;
  margin: 0;
`;

const changeStyles = css`
  color: #16a34a;
  font-weight: 700;
  font-size: 20px;
  text-align: center;
  margin: 0;
`;

const subLabelStyles = css`
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  margin: 0;
`;

const chartContainerStyles = css`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
  margin-bottom: 16px;
`;

const percentageStyles = css`
  font-weight: 700;
  font-size: 36px;
  text-align: center;
  margin-top: -12px;
  margin-bottom: 0;
`;

const PieChart = ({ children, width, height }: { children: React.ReactNode; width: number; height: number }) => (
  <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
    {children}
  </svg>
);

const Pie = ({
  dataKey,
  startAngle,
  endAngle,
  data,
  cx,
  cy,
  innerRadius,
  outerRadius,
  fill,
  stroke,
  isAnimationActive
}: any) => {
  const total = data.reduce((sum: number, entry: any) => sum + entry[dataKey], 0);
  let currentAngle = startAngle;

  const angleRange = Math.abs(endAngle - startAngle);

  return (
    <g>
      {data.map((entry: any, index: number) => {
        const segmentAngle = (entry[dataKey] / total) * angleRange;

        // Convert to radians (0° = right, counter-clockwise)
        // FIXED: Use the correct angle calculation for left-to-right direction
        const startAngleRad = (currentAngle - 90) * RADIAN;
        const endAngleRad = (currentAngle + segmentAngle - 90) * RADIAN;

        const outerStartX = cx + outerRadius * Math.cos(startAngleRad);
        const outerStartY = cy + outerRadius * Math.sin(startAngleRad);
        const outerEndX = cx + outerRadius * Math.cos(endAngleRad);
        const outerEndY = cy + outerRadius * Math.sin(endAngleRad);

        const innerStartX = cx + innerRadius * Math.cos(endAngleRad);
        const innerStartY = cy + innerRadius * Math.sin(endAngleRad);
        const innerEndX = cx + innerRadius * Math.cos(startAngleRad);
        const innerEndY = cy + innerRadius * Math.sin(startAngleRad);

        const largeArcFlag = segmentAngle > 180 ? 1 : 0;

        const pathData = [
          `M ${outerStartX} ${outerStartY}`,
          `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${outerEndX} ${outerEndY}`,
          `L ${innerStartX} ${innerStartY}`,
          `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${innerEndX} ${innerEndY}`,
          'Z'
        ].join(' ');

        currentAngle += segmentAngle;

        return (
          <path
            key={index}
            d={pathData}
            fill={entry.fill || fill}
            stroke={stroke}
          />
        );
      })}
    </g>
  );
};

export default function CapacityGaugeCard({
  isAnimationActive = true,
  value = 15,
  percentage = 65,
  label = "Server Utilization",
  subLabel = "Current vs. Maximum",
  change = "+10%",
  needleColor = "#ff6b6b",
  usedColor = "#ff6b6b",
  remainingColor = "#e8e8e8",
}: CapacityGaugeCardProps) {
  const cx = 150;
  const cy = 120;
  const iR = 80;
  const oR = 110;

  // Ensure value is between 0 and 100
  const clampedValue = 80;

  // Create data based on the value (0-100)
  const data = [
    { name: "Used", value: clampedValue, fill: usedColor },
    { name: "Remaining", value: 100 - clampedValue, fill: remainingColor },
  ];

  return (
    <div css={cardStyles}>
      <div css={sectionStyles}>
        <div css={groupStyles}>
          <h3 css={titleStyles}>{label}</h3>
          <p css={changeStyles}>{change}</p>
        </div>
        <p css={subLabelStyles}>{subLabel}</p>
      </div>

      <div css={sectionStyles}>
        <div css={chartContainerStyles}>
          <PieChart width={300} height={200}>
            <Pie
              dataKey="value"
              startAngle={0}  // Start from left (0°)
              endAngle={180}  // End at right (180°)
              data={data}
              cx={cx}
              cy={cy}
              innerRadius={iR}
              outerRadius={oR}
              fill="#8884d8"
              stroke="none"
              isAnimationActive={isAnimationActive}
            />
            <CustomNeedle
              value={clampedValue}
              cx={cx}
              cy={cy}
              iR={iR}
              oR={oR}
              color={needleColor}
            />
          </PieChart>
        </div>
      </div>

      <p css={percentageStyles}>{percentage}%</p>
    </div>
  );
}